/**
 * menu-tree — immutable tree helpers dla MenuItem (ADR-0031).
 *
 * Wzorzec z F12.N `layout-tree.ts`, ale uproszczony bo MenuItem ma tylko
 * jedną ścieżkę zagnieżdżenia (`children`) dla sekcji. Brak tabs/accordion/steps.
 *
 * Wszystkie funkcje zwracają nowy array (zero mutacji), żeby React reconciler
 * widział zmianę przez referencyjne porównanie.
 */

import type { MenuItem } from '@saas/shared';

export function findInTree(items: MenuItem[], id: string): MenuItem | null {
  for (const it of items) {
    if (it.id === id) return it;
    if (it.children && it.children.length > 0) {
      const found = findInTree(it.children, id);
      if (found) return found;
    }
  }
  return null;
}

export interface ContainerLocation {
  /** ID kontenera (sekcji) zawierającego element. `null` = top-level. */
  parentId: string | null;
  /** Array items którego elementem jest szukany id (do reorder przez findIndex). */
  items: MenuItem[];
}

/**
 * Znajduje kontener (parentId + lista items) zawierający element o `id`.
 * Używane przez MenuCanvas do detect reorder vs cross-container w handleDragEnd.
 */
export function findContainerOf(
  items: MenuItem[],
  id: string,
  parentId: string | null = null,
): ContainerLocation | null {
  for (const it of items) {
    if (it.id === id) return { parentId, items };
    if (it.children && it.children.length > 0) {
      const found = findContainerOf(it.children, id, it.id);
      if (found) return found;
    }
  }
  return null;
}

export function removeFromTree(items: MenuItem[], id: string): MenuItem[] {
  return items
    .filter((it) => it.id !== id)
    .map((it) =>
      it.children && it.children.length > 0
        ? { ...it, children: removeFromTree(it.children, id) }
        : it,
    );
}

export function updateInTree(
  items: MenuItem[],
  id: string,
  updater: (item: MenuItem) => MenuItem,
): MenuItem[] {
  return items.map((it) => {
    if (it.id === id) return updater(it);
    if (it.children && it.children.length > 0) {
      return { ...it, children: updateInTree(it.children, id, updater) };
    }
    return it;
  });
}

/**
 * Wstawia element do kontenera. `containerId=null` = top-level append.
 * Jeśli `containerId` nie istnieje albo nie jest sekcją — no-op (oryginalny array).
 */
export function insertIntoContainer(
  items: MenuItem[],
  containerId: string | null,
  child: MenuItem,
  index?: number,
): MenuItem[] {
  if (containerId === null) {
    const i = index ?? items.length;
    return [...items.slice(0, i), child, ...items.slice(i)];
  }
  return items.map((it) => {
    if (it.id === containerId) {
      if (it.type !== 'section') return it; // tylko sekcja może mieć dzieci
      const existing = it.children ?? [];
      const i = index ?? existing.length;
      return { ...it, children: [...existing.slice(0, i), child, ...existing.slice(i)] };
    }
    if (it.children && it.children.length > 0) {
      return { ...it, children: insertIntoContainer(it.children, containerId, child, index) };
    }
    return it;
  });
}

/**
 * Przesuwa element z dowolnego miejsca w drzewie do `targetContainerId` (lub top-level
 * gdy null). Implementacja: usuń source → znajdź source w usuniętym kontekście NIE
 * jest możliwe, więc znajdź PRZED usunięciem, potem wstaw.
 */
export function moveInTree(
  items: MenuItem[],
  sourceId: string,
  targetContainerId: string | null,
  targetIndex: number,
): MenuItem[] {
  const source = findInTree(items, sourceId);
  if (!source) return items;
  // Sekcja do samej siebie: po usunięciu źródła nie byłoby już kontenera docelowego
  // i element zniknąłby z drzewa.
  if (targetContainerId === sourceId) return items;
  // Cycle guard: nie pozwalamy przenieść sekcji do własnego subdrzewa.
  if (source.type === 'section' && targetContainerId !== null) {
    if (isDescendantOf(source, targetContainerId)) return items;
  }
  const withoutSource = removeFromTree(items, sourceId);
  return insertIntoContainer(withoutSource, targetContainerId, source, targetIndex);
}

/**
 * Reorder w obrębie tego samego kontenera (drag w pionie).
 * `containerId=null` = top-level.
 */
export function reorderInContainer(
  items: MenuItem[],
  containerId: string | null,
  fromIndex: number,
  toIndex: number,
): MenuItem[] {
  if (containerId === null) {
    return arrayMove(items, fromIndex, toIndex);
  }
  return items.map((it) => {
    if (it.id === containerId) {
      if (!it.children || it.children.length === 0) return it;
      return { ...it, children: arrayMove(it.children, fromIndex, toIndex) };
    }
    if (it.children && it.children.length > 0) {
      return { ...it, children: reorderInContainer(it.children, containerId, fromIndex, toIndex) };
    }
    return it;
  });
}

// ---------------------------------------------------------------------------
// helpers wewnętrzne
// ---------------------------------------------------------------------------

function arrayMove<T>(arr: T[], from: number, to: number): T[] {
  if (from === to) return arr;
  const copy = arr.slice();
  const [el] = copy.splice(from, 1);
  if (el === undefined) return arr;
  copy.splice(to, 0, el);
  return copy;
}

function isDescendantOf(node: MenuItem, candidateAncestorId: string): boolean {
  if (!node.children) return false;
  for (const c of node.children) {
    if (c.id === candidateAncestorId) return true;
    if (isDescendantOf(c, candidateAncestorId)) return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// menuItemsEqual — deep equality dla MenuItem[] po whitelisted keys.
//
// Po co: JSON.stringify do dirty flag daje false-positives — Postgres JSONB
// serializuje klucze po długości+alfabetycznie, frontend trzyma insertion
// order. Po pierwszym save+refetch obiekt z bazy może mieć inną kolejność
// kluczy niż wersja lokalna, mimo identycznej zawartości.
//
// Semantyka:
//   - INSENSITIVE na kolejność KLUCZY w obiekcie (iteracja po MENU_ITEM_KEYS
//     whitelist, nie Object.keys) — chroni przed JSONB round-trip.
//   - SENSITIVE na kolejność ELEMENTÓW w array (porównanie a[i]==b[i]) —
//     intencjonalnie, bo reorder w canvasie ma flipować dirty=true.
// ---------------------------------------------------------------------------

const MENU_ITEM_KEYS: ReadonlyArray<keyof MenuItem> = [
  'id',
  'type',
  'moduleCode',
  'itemKey',
  'areaCode',
  'url',
  'external',
  'label',
  'iconName',
  'collapsed',
  // Referencje widoków pozycji (akordeon, 2026-07-02) — samo przypisanie layoutu
  // formularza/listy musi flipować dirty (inaczej Zapisz zostaje wyłączony).
  'viewRef',
  'listRef',
];

export function menuItemsEqual(a: MenuItem[], b: MenuItem[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (!menuItemEqual(a[i]!, b[i]!)) return false;
  }
  return true;
}

function menuItemEqual(a: MenuItem, b: MenuItem): boolean {
  for (const key of MENU_ITEM_KEYS) {
    if (a[key] !== b[key]) return false;
  }
  // `view` (MenuItemView — listConfig/formConfig/baseFilters) nie jest prymitywem,
  // więc nie trafia do MENU_ITEM_KEYS. Porównujemy serializacją JSON — JSONB round-trip
  // nie zmienia zawartości view (klucze w tym obiekcie są stabilne, nie zależą od insertion
  // order jak zewnętrzne MenuItem).
  if (JSON.stringify(a.view ?? null) !== JSON.stringify(b.view ?? null)) return false;
  const aChildren = a.children ?? [];
  const bChildren = b.children ?? [];
  return menuItemsEqual(aChildren, bChildren);
}
